import { isAbortError } from '../abort'
import { errorAnalyticsPayload } from './errorAnalytics'
import { trackEvent } from './trackEvent'

interface ApiAnalyticsOptions<T = unknown> {
  eventName: string
  additionalProperties?: Record<string, unknown>
  getSuccessProperties?: (result: T) => Record<string, unknown>
}

export async function withAnalyticsTracking<T>(
  apiCall: () => Promise<T>,
  options: ApiAnalyticsOptions<T>,
): Promise<T> {
  const { eventName, additionalProperties = {}, getSuccessProperties } = options
  const startEventName = `${eventName} - Started`
  const successEventName = `${eventName} - Success`
  const cancelledEventName = `${eventName} - Canceled`
  const failureEventName = `${eventName} - Failed`
  const start = performance.now()

  try {
    // Capture start event
    trackEvent(startEventName, additionalProperties)

    const result = await apiCall()
    const duration = performance.now() - start

    // Merge additional success properties if provided
    const successProperties = getSuccessProperties ? getSuccessProperties(result) : {}

    // Capture success event
    trackEvent(successEventName, {
      ...additionalProperties,
      ...successProperties,
      duration,
    })

    return result
  } catch (error: unknown) {
    const duration = performance.now() - start

    // Abort errors are expected; track cancellation separately and rethrow
    if (isAbortError(error)) {
      trackEvent(cancelledEventName, {
        ...additionalProperties,
        duration,
      })
      throw error
    }
    // Capture failure event
    trackEvent(failureEventName, {
      ...additionalProperties,
      ...errorAnalyticsPayload(error),
    })
    throw error
  }
}
